import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import {
  CHART_AXIS_STROKE,
  CHART_AXIS_TICK_STYLE,
  CHART_GRID_STROKE,
  CHART_PALETTE,
} from "./chartConstants";
import {
  TooltipDot,
  TooltipLabel,
  TooltipRow,
  TooltipWrapper,
} from "./DashboardTooltip.styles";

// Likert series colors mapped onto the dashboard's CHART_PALETTE so the
// mock blends with the other widgets. Reads left-to-right as a
// positive → negative gradient.
const PROM_COLORS = {
  stronglyAgree: CHART_PALETTE.success,
  agree: CHART_PALETTE.secondary,
  disagree: CHART_PALETTE.warning,
  stronglyDisagree: CHART_PALETTE.error,
};

// Static mock of an Oxygen Monitor Program satisfaction survey. The real
// PROM data feed is intentionally bypassed for the dashboard demo — flip the
// chart back to the live `usePromResults` query whenever it's wired up.
interface IPromMockRow {
  category: string;
  fullText: string;
  stronglyAgree: number;
  agree: number;
  disagree: number;
  stronglyDisagree: number;
}

const MOCK_DATA: IPromMockRow[] = [
  {
    category: "Q1",
    fullText:
      "The Oxygen monitor helped me feel comfortable managing my respiratory condition at home.",
    stronglyAgree: 20,
    agree: 10,
    disagree: 15,
    stronglyDisagree: 2,
  },
  {
    category: "Q2",
    fullText: "The oxygen monitor was easy to use.",
    stronglyAgree: 20,
    agree: 25,
    disagree: 2,
    stronglyDisagree: 0,
  },
  {
    category: "Q3",
    fullText:
      "I was able to contact a member of the team with questions or concerns as needed.",
    stronglyAgree: 30,
    agree: 14,
    disagree: 3,
    stronglyDisagree: 0,
  },
  {
    category: "Q4",
    fullText:
      "I would recommend the oxygen monitor to others with similar respiratory conditions.",
    stronglyAgree: 29,
    agree: 10,
    disagree: 3,
    stronglyDisagree: 2,
  },
];

// EN-5191: a factory, not a module constant. Evaluated at import time, t() returns undefined
// and Recharts silently falls back to the raw dataKey in the legend ("stronglyAgree").
const buildSeries = (
  t: TFunction
): Array<{
  dataKey: keyof Omit<IPromMockRow, "category" | "fullText">;
  label: string;
  color: string;
}> => [
  {
    dataKey: "stronglyAgree",
    label: t("reports_charts_stronglyAgree"),
    color: PROM_COLORS.stronglyAgree,
  },
  {
    dataKey: "agree",
    label: t("reports_charts_agree"),
    color: PROM_COLORS.agree,
  },
  {
    dataKey: "disagree",
    label: t("reports_charts_disagree"),
    color: PROM_COLORS.disagree,
  },
  {
    dataKey: "stronglyDisagree",
    label: t("reports_charts_stronglyDisagree"),
    color: PROM_COLORS.stronglyDisagree,
  },
];

function PromTooltip({
  active,
  payload,
  label,
}: TooltipProps<ValueType, NameType>) {
  if (!active || !payload || payload.length === 0) return null;

  const row = MOCK_DATA.find((d) => d.category === label);

  return (
    <TooltipWrapper>
      <TooltipLabel>{row?.fullText ?? label}</TooltipLabel>
      {payload.map((entry) => (
        <TooltipRow key={String(entry.dataKey)}>
          <TooltipDot style={{ backgroundColor: entry.color ?? "#7562A8" }} />
          <span>
            {entry.name}: <strong>{entry.value}</strong>
          </span>
        </TooltipRow>
      ))}
    </TooltipWrapper>
  );
}

// Greedy word-wrap into <=charsPerLine character lines. Used by the x-axis
// tick renderer to wrap the full question text under each group.
function wrapText(text: string, charsPerLine: number): string[] {
  const lines: string[] = [];
  text.split(" ").forEach((word) => {
    const last = lines[lines.length - 1];
    if (last !== undefined && last.length + 1 + word.length <= charsPerLine) {
      lines[lines.length - 1] = `${last} ${word}`;
    } else {
      lines.push(word);
    }
  });
  return lines;
}

const TICK_CHARS_PER_LINE = 22;
const TICK_LINE_HEIGHT = 12;

interface IQuestionTickProps {
  x?: number;
  y?: number;
  payload?: { value?: string };
}

function QuestionTick({ x = 0, y = 0, payload }: IQuestionTickProps) {
  const row = MOCK_DATA.find((d) => d.category === payload?.value);
  const text = row?.fullText ?? payload?.value ?? "";
  const lines = wrapText(text, TICK_CHARS_PER_LINE);
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor="middle"
        fill={CHART_AXIS_TICK_STYLE.fill}
        fontSize={CHART_AXIS_TICK_STYLE.fontSize}
        fontFamily={CHART_AXIS_TICK_STYLE.fontFamily}
      >
        {lines.map((line, idx) => (
          <tspan
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            x={0}
            dy={TICK_LINE_HEIGHT}
          >
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}

export function PromResultsChart() {
  const { t } = useTranslation();
  const series = useMemo(() => buildSeries(t), [t]);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={MOCK_DATA}
        margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
        barCategoryGap={16}
      >
        <CartesianGrid
          stroke={CHART_GRID_STROKE}
          strokeDasharray="3 3"
          vertical={false}
        />
        <XAxis
          dataKey="category"
          stroke={CHART_AXIS_STROKE}
          tick={<QuestionTick />}
          tickMargin={8}
          interval={0}
          height={80}
        />
        <YAxis
          stroke={CHART_AXIS_STROKE}
          tick={CHART_AXIS_TICK_STYLE}
          allowDecimals={false}
          width={40}
        />
        <Tooltip
          cursor={{ fill: "rgba(117, 98, 168, 0.06)" }}
          content={(p) => <PromTooltip {...p} />}
        />
        <Legend
          verticalAlign="top"
          height={28}
          iconType="circle"
          wrapperStyle={{ fontSize: 11 }}
        />
        {series.map((s) => (
          <Bar
            key={s.dataKey}
            dataKey={s.dataKey}
            name={s.label}
            fill={s.color}
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
